/** Map FinancialData.net house-feed Mongo docs (GET /shadow-money, /ticker-talk) onto cockpit charts.
 * Does not invent Q/Q 13F flow, ADV dilution series, party timelines, or sector heatmaps.
 */
import { STOCK_ACT_BRACKETS } from "@/cockpit/formatCockpit";

const CONSTRUCTIVE = /\b(beat|beats|raise|raises|upgrade|record|surge|soar|wins?|growth|profit)\b/i;
const CAUTIOUS = /\b(miss|misses|missed|downgrade|downgrades|downgraded|cut|cuts|cutting|probe|lawsuit|warning|recall|fraud|plunge|plunges|plunged|layoff|layoffs|drop|drops|dropped|decline|declines|declined|falls|fell|fallen|loss|losses|slump|slumps|crash|crashes|tumble|tumbles|tumbled|slide|slides|slid|sink|sinks|sank|weak|weakness|shortfall|disappoint|disappoints|disappointed|halt|halts|halted|pause|pauses|paused|suspend|suspended|delay|delays|delayed|slowdown|contraction|bankruptcy|default|sell[- ]?off|investigation|penalty)\b/i;
const STORED_DIRECTION = new Set(["constructive", "cautious", "bullish", "bearish", "positive", "negative"]);

function hasTerm(re, text) {
  re.lastIndex = 0;
  return re.test(text);
}

export function tagHeadline(text) {
  const blob = String(text || "");
  const c = hasTerm(CONSTRUCTIVE, blob);
  const k = hasTerm(CAUTIOUS, blob);
  if (c && !k) return "constructive";
  if (k && !c) return "cautious";
  return "neutral";
}

function headlineBadge(item) {
  const stored = String(item?.sentiment || "").toLowerCase();
  const tag = STORED_DIRECTION.has(stored) ? stored : tagHeadline(`${item?.headline || ""} ${item?.summary || ""}`);
  if (tag === "constructive" || tag === "positive" || tag === "bullish") return "Bullish";
  if (tag === "cautious" || tag === "negative" || tag === "bearish") return "Bearish";
  return "Neutral";
}

export function parseAmountBracket(raw) {
  const s = String(raw || "").trim();
  if (!s) return { label: "—", mid: 0 };
  const known = STOCK_ACT_BRACKETS.find((b) => s === b.label || s.replace(/\s/g, "") === b.label.replace(/\s/g, ""));
  if (known) return { label: known.label, mid: known.mid };
  const nums = [...s.replace(/,/g, "").matchAll(/(\d+(?:\.\d+)?)/g)].map((m) => Number(m[1]));
  if (nums.length >= 2) return { label: s, mid: Math.round((nums[0] + nums[1]) / 2) };
  if (nums.length === 1) return { label: s, mid: Math.round(nums[0]) };
  return { label: s, mid: 0 };
}

function insiderSide(tx) {
  const code = String(tx.transaction_code || "").toUpperCase();
  const ad = String(tx.acquired_or_disposed || "").toUpperCase();
  if (code === "P" || ad === "A") return "Buy";
  if (code === "S" || ad === "D") return "Sell";
  return "Exchange";
}

function ptrSide(type) {
  const t = String(type || "");
  if (/purchase|buy|acquired/i.test(t)) return "Buy";
  if (/sale|sell|disposed/i.test(t)) return "Sell";
  return "Exchange";
}

function ptrChamber(raw) {
  const s = String(raw || "");
  if (/senate/i.test(s)) return "Senate";
  if (/house|representative/i.test(s)) return "House";
  return s || "—";
}

export function mapShadowDoc(doc) {
  const ticker = doc?.ticker || "";
  const holders = (doc?.institutions?.top_holders || []).map((h) => ({
    investorName: h.investor_name || "—",
    positionValueUsd: Number(h.market_value) || 0,
    shares: Number(h.shares) || 0,
    newPositionUsd: 0,
    exitUsd: 0,
    periodOfReport: doc?.institutions?.period_of_report || null,
  }));
  const insiderActivity = (doc?.insider?.latest || []).map((tx, i) => {
    const shares = Number(tx.shares) || 0;
    const price = Number(tx.price) || 0;
    const valueUsd = shares && price ? shares * price : shares;
    return {
      id: `${ticker}-f4-${tx.date || i}-${i}`,
      date: tx.date || "",
      valueUsd: Number.isFinite(valueUsd) ? valueUsd : 0,
      shares,
      type: insiderSide(tx),
      insiderName: tx.insider_name || "—",
      transactionCode: tx.transaction_code,
      role: tx.role,
    };
  });
  const politicalTrades = (doc?.congress || []).map((c, i) => {
    const br = parseAmountBracket(c.amount);
    return {
      id: `${ticker}-ptr-${c.date || i}-${i}`,
      politicianName: c.person || "—",
      party: "",
      chamber: ptrChamber(c.chamber),
      type: ptrSide(c.type),
      amountBracket: br.label,
      amountMidpointUsd: br.mid,
      tradeDate: c.date || "",
      disclosureDate: c.date || "",
      delayDays: 0,
      delayFlag: false,
      assetType: c.asset_type || c.instrument || "Unknown/not disclosed",
    };
  });
  const proposedSales = (doc?.proposed_sales || []).map((s, i) => ({
    id: `${ticker}-144-${s.date || i}-${i}`,
    date: s.date || "",
    seller: s.seller || "—",
    role: s.role || "",
    shares: Number(s.shares) || 0,
    marketValueUsd: Number(s.market_value) || 0,
  }));
  return {
    ticker,
    updatedAt: doc?.updated_at || null,
    source: doc?.source || "financialdata_net",
    institutionsNote: doc?.institutions?.note || "",
    buyCount30d: doc?.insider?.buy_count_30d || 0,
    sellCount30d: doc?.insider?.sell_count_30d || 0,
    institutionalFlows: [],
    institutionalHolders: holders,
    insiderActivity,
    politicalTimeline: [],
    politicalTrades,
    dilutionSeries: [],
    proposedSales,
    dilutionDataSourceStatus: "fdn-mongo",
    sectorPoliticalFlow: [],
  };
}

export function mapTalkDoc(doc) {
  const ticker = doc?.ticker || "";
  const feed = (doc?.items || []).map((it, i) => ({
    id: `${it.ticker || ticker}-news-${i}`,
    timestamp: it.date || doc?.updated_at || "",
    ticker: it.ticker || ticker,
    sentimentBadge: headlineBadge(it),
    impactScore: null,
    clusterSize: null,
    headline: it.ticker && it.ticker !== ticker
      ? `${it.ticker} · ${it.headline || ""}`
      : (it.headline || ""),
    url: it.url || it.article_url || it.link || null,
    summary: it.summary || "",
    sentimentSource: it.sentiment_source || "headline_keywords",
    kind: it.kind,
  }));
  return {
    ticker,
    updatedAt: doc?.updated_at || null,
    source: doc?.source || "financialdata_net",
    tone: doc?.tone || "quiet",
    sectorComparison: null,
    sentimentOscillator: [],
    feed,
    sectorHeatmap: [],
    advanceDecline: [],
  };
}

export function inLookback(dateStr, days) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) <= days * 86400000;
}
